/*
 *   This content is licensed according to the W3C Software License at
 *   https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 *
 *   Desc:   Tablist widget that implements ARIA Authoring Practices
 */

import eventBus from '@refapp/eventbus-js';

class Tablist {
  constructor(groupNode, isManual = false) {
    this.tablistNode = groupNode;
    this.isManual = isManual; // This flag determines if the tabs should behave manually or automatically

    this.tabs = [];
    this.firstTab = null;
    this.lastTab = null;

    this.tabs = Array.from(this.tablistNode.querySelectorAll('[role=tab]'));
    this.tabpanels = [];

    for (let tab of this.tabs) {
      const tabpanel = document.getElementById(tab.getAttribute('aria-controls'));

      tab.tabIndex = -1;
      // eslint-disable-next-line sonarjs/no-duplicate-string
      tab.setAttribute('aria-selected', 'false');
      this.tabpanels.push(tabpanel);

      tab.addEventListener('keydown', this.onKeydown.bind(this));

      if (this.isManual) {
        tab.addEventListener('click', this.onClick.bind(this)); // Only for manual interaction
      }

      if (!this.firstTab) {
        this.firstTab = tab;
      }
      this.lastTab = tab;
    }

    this.setSelectedTab(this.firstTab, false);
  }

  setSelectedTab(currentTab, setFocus = true) {
    for (let tab of this.tabs) {
      if (currentTab === tab) {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        tab.setAttribute('aria-selected', 'true');
        tab.removeAttribute('tabindex');

        if (!this.isManual) {
          eventBus.emit('tab-selected', tab.getAttribute('data-index'));
        }

        if (setFocus) {
          tab.focus();
        }
      } else {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;
      }
    }
  }

  setSelectedToPreviousTab(currentTab) {
    let index;
    if (currentTab === this.firstTab) {
      this.setSelectedTab(this.lastTab);
    } else {
      index = this.tabs.indexOf(currentTab);
      this.setSelectedTab(this.tabs[index - 1]);
    }
  }

  setSelectedToNextTab(currentTab) {
    let index;
    if (currentTab === this.lastTab) {
      this.setSelectedTab(this.firstTab);
    } else {
      index = this.tabs.indexOf(currentTab);
      this.setSelectedTab(this.tabs[index + 1]);
    }
  }

  moveFocusToTab(currentTab) {
    currentTab.focus();
  }

  moveFocusToPreviousTab(currentTab) {
    let index;
    if (currentTab === this.firstTab) {
      this.moveFocusToTab(this.lastTab);
    } else {
      index = this.tabs.indexOf(currentTab);
      this.moveFocusToTab(this.tabs[index - 1]);
    }
  }

  moveFocusToNextTab(currentTab) {
    let index;
    if (currentTab === this.lastTab) {
      this.moveFocusToTab(this.firstTab);
    } else {
      index = this.tabs.indexOf(currentTab);
      this.moveFocusToTab(this.tabs[index + 1]);
    }
  }

  /* EVENT HANDLERS */

  onKeydown(event) {
    const tgt = event.currentTarget;
    let flag = false;

    switch (event.key) {
      case 'ArrowLeft':
        if (this.isManual) {
          this.moveFocusToPreviousTab(tgt);
        } else {
          this.setSelectedToPreviousTab(tgt);
        }
        flag = true;
        break;

      case 'ArrowRight':
        if (this.isManual) {
          this.moveFocusToNextTab(tgt);
        } else {
          this.setSelectedToNextTab(tgt);
        }
        flag = true;
        break;

      case 'Home':
        if (this.isManual) {
          this.moveFocusToTab(this.firstTab);
        } else {
          this.setSelectedTab(this.firstTab);
        }
        flag = true;
        break;

      case 'End':
        if (this.isManual) {
          this.moveFocusToTab(this.lastTab);
        } else {
          this.setSelectedTab(this.lastTab);
        }
        flag = true;
        break;

      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  onClick(event) {
    this.setSelectedTab(event.currentTarget);
  }
}

export default Tablist;
